import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';

import RecordBadge, { RecordTitle } from '../common/RecordBadge';
import { cardOwnerOf } from './CardVisuals';
import GroupHeader from '../common/GroupHeader';
import DataTypeBadge from '../common/DataTypeBadge';
import { formatCount, joinMeta } from '../../utils/counts';
import { cardCollectionsPath, childCardsPath } from '../../utils/listLinks';
import { describeStoredTemplate } from '../../utils/displayTemplate';
import { sampleDisplayValue } from '../../utils/displaySample';
import { groupElementsIntoPanels } from '../../utils/elements';
import { formLayoutPanels } from '../../utils/formLayout';
import { isFormulaDriven } from '../../utils/formulaTokens';
import { getMeasure } from '../../utils/units';
import { defaultUnitFor, measuresLabel, unitSymbol } from '../../utils/measurements';
import { normalizeId } from '../../utils/ids';
import { columnsOf, isRepeatingGroup } from '../../config/repeatingGroups';
import { cardPagePath, chainElementSets, mergeChainEntries, resolveChain } from '../../utils/cards';
import { elementSetPath } from '../../utils/elementSets';
import { CARD_TAB } from './cardTabs';
import { governingGrant } from '../../utils/elementAccess';
import { choiceListOf } from '../../utils/choiceValues';

/**
 * What an element's rule says, in the few words a home row has room for —
 * or null when nothing narrows it (no rule, or a rule with no levels).
 */
const accessFact = (governing) => {
	const grant = governing?.grant;
	if (!grant) return null;
	const limited = Array.isArray(grant.rules) && grant.rules.length > 0;
	const parts = [];
	if (limited) parts.push('Access limited');
	if (grant.keeprOnly === true) parts.push('Only in keepr');
	return parts.length ? parts.join(' · ') : null;
};

/**
 * THE CARD HOME — what `/cards/:id` shows with no `?tab=`.
 *
 * Read-only and typographic: no field labels, no panels of inputs, nothing
 * to save. It says what the card IS, in the platform's own parts —
 * `RecordTitle` over a meta line of counts (each one a door to the list of
 * exactly those records), the card's badge wearing a SAMPLE of the title
 * its items get, its elements as light rows with their `DataTypeBadge`,
 * its children, and the collections that use it. Every `GroupHeader`
 * carries a small "Edit" door to the tab that changes it.
 *
 * It always renders the SAVED card (`CardDefinitionForm` passes
 * `savedCard`, never the draft): a home shows what is, and the editing
 * tabs carry the dot that says what is not saved yet.
 */

/** One muted fact line of nodes: counts as words, doors as links. */
const MetaLine = ({ parts, className = '' }) => {
	const kept = parts.filter(Boolean);
	if (!kept.length) return null;
	return (
		<div className={['card-home__meta', 'meta-line', className].filter(Boolean).join(' ')}>
			{kept.map((part, index) => (
				<Fragment key={part.key || index}>
					{index > 0 ? <span className="card-home__dot" aria-hidden="true"> · </span> : null}
					{part}
				</Fragment>
			))}
		</div>
	);
};

/** The muted facts beside an element's name, in the editor row's words. */
const elementFacts = (element, fromCard, fromSet) => {
	const facts = [];
	if (element?.options?.required) facts.push('required');
	const choices = choiceListOf(element).length;
	if (choices) facts.push(`${choices} choice${choices === 1 ? '' : 's'}`);
	const measure = element?.dataType === 'measurement' ? getMeasure(element?.options?.measure) : null;
	// Several measures (KPR-1085): "Volume, mass or count · cup".
	if (measure) facts.push(`${measuresLabel(element.options) || measure.label} · ${unitSymbol(defaultUnitFor(element.options))}`);
	// A money element's currencies ("USD · CAD"), the editor row's chip.
	const currencies = element?.dataType === 'currency' && Array.isArray(element?.options?.currencies) ? element.options.currencies : [];
	if (currencies.length) facts.push(currencies.slice(0, 3).join(' · ') + (currencies.length > 3 ? ` +${currencies.length - 3}` : ''));
	// A repeating group's size (KPR-679): its columns, and the most entries
	// when the author set one.
	if (isRepeatingGroup(element)) {
		const columns = columnsOf(element).length;
		facts.push(columns ? formatCount(columns, 'column') : 'no columns');
		if (Number.isInteger(element?.options?.maxEntries)) facts.push(`up to ${formatCount(element.options.maxEntries, 'entry', 'entries')}`);
	}
	if (isFormulaDriven(element)) facts.push('calculated');
	else if (element?.options?.drivenFrom) facts.push('driven');
	// Where the definition came from: a set ("from Contact"), else an
	// ancestor ("from Person"); a card's own element says nothing.
	if (fromSet) facts.push(`from ${fromSet?.name || 'an element set'}`);
	else if (fromCard) facts.push(`from ${fromCard?.name || 'a parent card'}`);
	return joinMeta(...facts);
};

/**
 * A repeating group's COLUMNS, each with its own door to who can see or
 * change it (KPR-688): a column follows the group until its own rule narrows
 * it. Shown where the group's own door is (a manager's), under the group.
 * Only a saved column (one with an id) can carry a rule.
 */
const GroupColumnsAccess = ({ group, groupLabel, access }) => {
	const columns = columnsOf(group).filter(column => column?.id);
	if (!columns.length) return null;
	return (
		<ul className="card-home__columns" aria-label={`Columns of ${groupLabel}`}>
			{columns.map((column) => {
				const label = column?.label?.singular || column.name;
				const words = accessFact(governingGrant(access.grants, access.chainIds, column.id));
				return (
					<li key={column.id} className="card-home__column">
						<span className="card-home__element-label">{label}</span>
						<DataTypeBadge dataType={column?.dataType} size="sm" />
						{words ? <span className="card-home__element-access-fact">{words}</span> : null}
						<Button
							type="link"
							size="small"
							className="card-home__element-access"
							aria-label={`Who can see or change ${label} in ${groupLabel}`}
							onClick={() => access.onOpen({ ...column, group })}
						>
							Access…
						</Button>
					</li>
				);
			})}
		</ul>
	);
};

export default function CardHome({
	card,
	cardId,
	// Parent-first ancestors of this card, for the lineage crumb and the
	// inherited half of the element list.
	parentChain = [],
	// The app-wide element sets, by id: every node's attached sets fold into
	// its elements (utils/cards resolveChain), and the sets themselves are
	// listed in their own section.
	elementSetsById = null,
	childCards = [],
	// The `include=usage` annotation: which collections use this card.
	usage = null,
	scopeTag = null,
	primaryDate = null,
	commentsAllowed = false,
	// Read-only viewers (global cards, a collection they don't manage) get
	// the home and nothing else: no tabs, so no Edit doors either.
	editable = false,
	// The form layout that applies (the card's own or an ancestor's —
	// useFormLayout), so the element list reads in the order and groups an
	// item form renders. Null: the automatic grouping.
	formLayout = null,
	// Who can see or change each element (KPR-509): `{ grants, chainIds,
	// onOpen(element) }` for a manager of the collection in hand while the
	// API offers element rules, else null — and then no row says anything
	// about access. Independent of `editable`: a manager may set rules on a
	// card they cannot edit (a global card, from their collection).
	elementAccess = null,
	// False inside the collection shell (KPR-1282): the page's own head already
	// leads with the card's tile and title, so the home starts at its description.
	showTitle = true,
}) {
	const cardVisual = { icon: card?.icon, color: card?.color, name: card?.name };
	const name = (card?.name || '').trim() || 'Untitled card';
	const description = (card?.description || '').trim();

	// Own elements plus inherited ones — what an item form actually renders.
	// Parent-first through the platform's one merge rule (utils/cards
	// mergeChainEntries: a child's re-declaration of a name wins, in the
	// ancestor's position), and each entry remembers which card supplied the
	// WINNING definition so the row says "from Person" only when the
	// parent's definition is the one an item gets.
	// Each node is resolved with its element sets first, so a set's elements
	// are in the list (grouped under the set's name) and say "from Contact".
	const resolvedChain = useMemo(
		() => resolveChain([...parentChain, card].filter(Boolean), elementSetsById),
		[parentChain, card, elementSetsById]
	);
	const ownId = normalizeId(card?._id);
	const mergedEntries = useMemo(() => (
		mergeChainEntries(resolvedChain)
			.map(({ element, card: node, set }) => ({
				element,
				fromCard: (ownId ? normalizeId(node?._id) === ownId : node === card) ? null : node,
				fromSet: set,
			}))
	), [resolvedChain, ownId, card]);
	// The sets this card carries — its own and its ancestors' — with the
	// card that attaches each.
	const elementSets = useMemo(() => chainElementSets(resolvedChain), [resolvedChain]);

	const panels = useMemo(
		() => formLayoutPanels(mergedEntries, formLayout?.body, { getElement: entry => entry.element })
			|| groupElementsIntoPanels(mergedEntries, { getElement: entry => entry.element }),
		[mergedEntries, formLayout]
	);

	const elements = useMemo(() => mergedEntries.map(entry => entry.element), [mergedEntries]);

	// "Items read as": the template rendered over believable values, or the
	// default composition a card with no template gets.
	const sample = useMemo(
		() => sampleDisplayValue({ template: card?.displayTemplate, elements, name }),
		[card?.displayTemplate, elements, name]
	);
	const templateSummary = useMemo(() => {
		const labels = new Map(elements.map(element => [element.name, element?.label?.singular || element.name]));
		return describeStoredTemplate(card?.displayTemplate, labels);
	}, [card?.displayTemplate, elements]);

	// Where it is used: the owning collection first, then everyone else.
	const usedCollections = useMemo(() => {
		const out = [];
		const seen = new Set();
		const push = (collection) => {
			const id = normalizeId(collection?._id);
			if (!collection || (id && seen.has(id))) return;
			if (id) seen.add(id);
			out.push(collection);
		};
		push(usage?.ownerCollection);
		(Array.isArray(usage?.collections) ? usage.collections : []).forEach(push);
		return out;
	}, [usage]);

	const cardRef = { key: card?.key, _id: cardId };
	const elementsText = formatCount(elements.length, 'element');
	const usedText = formatCount(usedCollections.length, 'collection');
	const childrenText = formatCount(childCards.length, 'child card');

	const editLink = (tab, label) => (editable ? (
		<Link className="card-home__edit" to={`?tab=${tab}`} replace aria-label={label}>Edit</Link>
	) : null);

	const primaryDateFact = primaryDate
		? `Primary date · ${primaryDate.label || 'Created'}`
		: null;

	return (
		<div className="card-home">

			{/* Head: the card's own title, its description, and one line of
			    facts whose counts are doors. */}
			<div className="card-home__head">
				{showTitle ? (
					<RecordTitle
						kind="card"
						record={cardVisual}
						title={name}
						size="lg"
						wrap
						className="card-home__title"
					/>
				) : null}
				{description ? <p className="card-home__description">{description}</p> : null}
				<MetaLine parts={[
					scopeTag ? <span key="scope" className="card-home__scope">{scopeTag}</span> : null,
					elementsText ? <span key="elements">{elementsText}</span> : null,
					usedText ? (
						<Link key="used" to={cardCollectionsPath(cardRef)}>{`Used in ${usedText}`}</Link>
					) : null,
					childrenText ? (
						<Link key="children" to={childCardsPath(cardRef)}>{childrenText}</Link>
					) : null,
				]} />

				{parentChain.length ? (
					<div className="card-home__lineage" aria-label="Lineage">
						{[...parentChain, null].map((node, index) => {
							const current = index === parentChain.length;
							const id = normalizeId(node?._id);
							return (
								<Fragment key={current ? 'current' : (id || index)}>
									{index > 0 ? <span className="card-home__lineage-sep" aria-hidden="true">›</span> : null}
									{current ? (
										<RecordTitle kind="card" record={cardVisual} title={name} size="sm" />
									) : (
										<RecordBadge
											kind="card"
											record={node}
											title={node?.name || '(untitled)'}
											size="sm"
											to={id ? cardPagePath(node) : undefined}
											collection={cardOwnerOf(node)}
										/>
									)}
								</Fragment>
							);
						})}
					</div>
				) : null}
			</div>

			<section className="card-home__section" aria-label="Items read as">
				<GroupHeader label="Items read as" action={editLink(CARD_TAB.display, 'Edit display value')} />
				<div className="card-home__sample">
					<RecordBadge
						kind="card"
						record={cardVisual}
						title={sample || name}
						size="md"
						wrap
					/>
					<p className="card-home__caption">
						{templateSummary || 'No template — an item is named by its first field, and the date if it has one.'}
					</p>
				</div>
			</section>

			<section className="card-home__section" aria-label="Elements">
				<GroupHeader
					label="Elements"
					count={elements.length}
					countLabel="element"
					action={editLink(CARD_TAB.elements, 'Edit elements')}
				/>
				{panels.length ? panels.map(panel => (
					<div key={panel.key} className="card-home__group">
						{panel.label ? <div className="micro-label card-home__group-label">{panel.label}</div> : null}
						<ul className="card-home__elements">
							{panel.entries.map(({ element, fromCard, fromSet }) => {
								const facts = elementFacts(element, fromCard, fromSet);
								const label = element?.label?.singular || element.name;
								// Rules name an element by its immutable id; one
								// that has none yet (never saved since ids came)
								// cannot carry one, so it gets no door.
								const access = elementAccess && element?.id ? elementAccess : null;
								const accessWords = access ? accessFact(governingGrant(access.grants, access.chainIds, element.id)) : null;
								return (
									<li key={element.name} className="card-home__element">
										<span className="card-home__element-label">
											{label}
										</span>
										<DataTypeBadge dataType={element?.dataType} size="sm" />
										{facts ? <span className="card-home__element-facts">{facts}</span> : null}
										{accessWords ? <span className="card-home__element-access-fact">{accessWords}</span> : null}
										{access ? (
											<Button
												type="link"
												size="small"
												className="card-home__element-access"
												aria-label={`Who can see or change ${label}`}
												onClick={() => access.onOpen(element)}
											>
												Access…
											</Button>
										) : null}
										{access && isRepeatingGroup(element) ? (
											<GroupColumnsAccess group={element} groupLabel={label} access={access} />
										) : null}
									</li>
								);
							})}
						</ul>
					</div>
				)) : (
					<p className="card-home__empty empty-line">
						No elements yet
						{editable ? <> · <Link to={`?tab=${CARD_TAB.elements}`} replace>Add one</Link></> : null}
					</p>
				)}
				<MetaLine
					className="card-home__cardwide"
					parts={[
						primaryDateFact ? <span key="primary-date">{primaryDateFact}</span> : null,
						<span key="comments">{`Comments ${commentsAllowed ? 'on' : 'off'}`}</span>,
					]}
				/>
			</section>

			{elementSets.length ? (
				<section className="card-home__section" aria-label="Element sets">
					<GroupHeader
						label="Element sets"
						count={elementSets.length}
						countLabel="set"
						action={editLink(CARD_TAB.elements, 'Edit element sets')}
					/>
					<div className="card-home__badges card-home__sets">
						{elementSets.map(({ set, card: node }) => {
							const inherited = ownId ? normalizeId(node?._id) !== ownId : node !== card;
							return (
								<span key={normalizeId(set?._id)} className="card-home__set">
									<RecordBadge
										kind="elementSet"
										record={set}
										title={set?.name || '(untitled set)'}
										size="sm"
										to={elementSetPath(set)}
									/>
									{inherited ? (
										<span className="card-home__set-from">{`from ${node?.name || 'a parent card'}`}</span>
									) : null}
								</span>
							);
						})}
					</div>
				</section>
			) : null}

			{childCards.length ? (
				<section className="card-home__section" aria-label="Child cards">
					<GroupHeader label="Child cards" count={childCards.length} countLabel="card" />
					<div className="card-home__badges">
						{childCards.map(child => (
							<RecordBadge
								key={normalizeId(child?._id)}
								kind="card"
								record={child}
								title={child?.name || '(untitled card)'}
								size="sm"
								to={cardPagePath(child)}
								collection={cardOwnerOf(child)}
							/>
						))}
					</div>
				</section>
			) : null}

			{usedCollections.length ? (
				<section className="card-home__section" aria-label="Where it is used">
					<GroupHeader label="Where it is used" count={usedCollections.length} countLabel="collection" />
					<div className="card-home__badges">
						{usedCollections.map(collection => (
							<RecordBadge
								key={normalizeId(collection?._id)}
								kind="collection"
								record={collection}
								title={collection?.name || '(untitled collection)'}
								size="sm"
								to={`/collections/${normalizeId(collection?._id)}/`}
							/>
						))}
					</div>
				</section>
			) : null}

		</div>
	);
}
